import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Leaf,
  ArrowLeft,
  LogOut,
  Upload,
  Trash2,
  Copy,
  Check,
  Image,
  Loader2,
  X,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const auth = sessionStorage.getItem("adminAuth");
  if (!auth) return null;
  return { Authorization: `Basic ${auth}` };
};

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const AdminImagesPage = () => {
  const navigate = useNavigate();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [copiedUrl, setCopiedUrl] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const headers = getAuthHeaders();

  useEffect(() => {
    if (!headers) {
      navigate("/admin");
      return;
    }
    fetchImages();
  }, []);

  const fetchImages = async () => {
    try {
      const response = await axios.get(`${API}/admin/images`, { headers });
      setImages(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        sessionStorage.removeItem("adminAuth");
        navigate("/admin");
      } else {
        toast.error("Failed to load images");
      }
    } finally {
      setLoading(false);
    }
  };

  const uploadFiles = async (files) => {
    if (!files || files.length === 0) return;

    setUploading(true);
    let successCount = 0;

    for (const file of files) {
      const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
      if (!allowedTypes.includes(file.type)) {
        toast.error(`${file.name}: Invalid file type`);
        continue;
      }

      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name}: File too large (max 10MB)`);
        continue;
      }

      const formData = new FormData();
      formData.append("file", file);

      try {
        await axios.post(`${API}/admin/upload`, formData, {
          headers: {
            ...headers,
            "Content-Type": "multipart/form-data",
          },
        });
        successCount++;
      } catch (error) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }

    if (successCount > 0) {
      toast.success(`${successCount} image${successCount > 1 ? "s" : ""} uploaded!`);
      fetchImages();
    }
    setUploading(false);
  };

  const handleFileSelect = (e) => {
    uploadFiles(e.target.files);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    uploadFiles(e.dataTransfer.files);
  };

  const handleDelete = async (filename) => {
    if (!window.confirm("Delete this image?")) return;

    try {
      await axios.delete(`${API}/admin/images/${filename}`, { headers });
      toast.success("Image deleted");
      fetchImages();
      if (selectedImage?.filename === filename) {
        setSelectedImage(null);
      }
    } catch (error) {
      toast.error("Failed to delete image");
    }
  };

  const copyUrl = (url) => {
    const fullUrl = `${window.location.origin}${url}`;
    navigator.clipboard.writeText(fullUrl);
    setCopiedUrl(url);
    toast.success("URL copied!");
    setTimeout(() => setCopiedUrl(null), 2000);
  };

  const handleLogout = () => {
    sessionStorage.removeItem("adminAuth");
    navigate("/admin");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-deep-moss font-serif text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bone-cream animate-fade-in">
      {/* Header */}
      <header className="bg-deep-moss text-white">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <Leaf className="w-6 h-6" />
              <span className="font-serif text-xl font-semibold">WEH Admin</span>
            </div>
            <button
              onClick={handleLogout}
              data-testid="logout-btn"
              className="flex items-center gap-2 font-sans text-sm text-white/80 hover:text-white"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 md:px-8 max-w-7xl py-8">
        <div className="flex items-center gap-4 mb-8">
          <Link
            to="/admin/dashboard"
            data-testid="back-to-dashboard"
            className="text-stone-500 hover:text-deep-moss"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <h1 className="font-serif text-3xl font-semibold text-stone-900">Image Library</h1>
        </div>

        {/* Upload Area */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp"
          multiple
          onChange={handleFileSelect}
          className="hidden"
          data-testid="image-file-input"
        />

        <div
          onDragEnter={() => setIsDragging(true)}
          onDragLeave={() => setIsDragging(false)}
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDrop}
          onClick={() => !uploading && fileInputRef.current?.click()}
          data-testid="upload-dropzone"
          className={`mb-8 p-8 border-2 border-dashed rounded-xl cursor-pointer transition-all ${
            isDragging
              ? "border-deep-moss bg-deep-moss/5"
              : "border-stone-300 hover:border-deep-moss hover:bg-stone-50"
          }`}
        >
          <div className="flex flex-col items-center justify-center text-center">
            {uploading ? (
              <>
                <Loader2 className="w-12 h-12 text-deep-moss animate-spin mb-4" />
                <p className="font-sans text-lg text-stone-700">Uploading...</p>
              </>
            ) : (
              <>
                <div className="w-16 h-16 rounded-full bg-deep-moss/10 flex items-center justify-center mb-4">
                  <Upload className="w-8 h-8 text-deep-moss" />
                </div>
                <p className="font-sans text-lg text-stone-700 mb-1">
                  <span className="text-deep-moss font-semibold">Click to upload</span> or drag and drop
                </p>
                <p className="font-sans text-sm text-stone-500">
                  JPG, PNG, GIF or WebP • Max 10MB per file • Multiple files allowed
                </p>
              </>
            )}
          </div>
        </div>

        {/* Images Grid */}
        <div className="grid lg:grid-cols-4 gap-8">
          <div className="lg:col-span-3">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-serif text-xl font-semibold text-stone-900">
                Your Images ({images.length})
              </h2>
            </div>

            {images.length === 0 ? (
              <div className="bg-white p-12 text-center border border-stone-200 rounded-lg">
                <Image className="w-12 h-12 mx-auto text-stone-300 mb-4" />
                <p className="font-sans text-stone-500">No images uploaded yet</p>
                <p className="font-sans text-sm text-stone-400 mt-1">
                  Upload images to use in your products and blog posts
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {images.map((image) => {
                  const fullUrl = image.url;
                  const isSelected = selectedImage?.filename === image.filename;
                  return (
                    <div
                      key={image.filename}
                      onClick={() => setSelectedImage(image)}
                      data-testid={`image-${image.filename}`}
                      className={`relative group cursor-pointer rounded-lg overflow-hidden border-2 transition-all ${
                        isSelected
                          ? "border-deep-moss ring-2 ring-deep-moss/20"
                          : "border-stone-200 hover:border-stone-300"
                      }`}
                    >
                      <div className="aspect-square">
                        <img
                          src={fullUrl}
                          alt={image.filename}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                        <div className="flex gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              copyUrl(image.url);
                            }}
                            className="p-2 bg-white rounded-full shadow-lg hover:bg-stone-50"
                            title="Copy URL"
                          >
                            {copiedUrl === image.url ? (
                              <Check className="w-4 h-4 text-green-600" />
                            ) : (
                              <Copy className="w-4 h-4 text-stone-600" />
                            )}
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(image.filename);
                            }}
                            className="p-2 bg-white rounded-full shadow-lg hover:bg-red-50"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Selected Image Details */}
          <div className="lg:col-span-1">
            {selectedImage ? (
              <div className="bg-white p-6 border border-stone-200 rounded-lg sticky top-24 animate-fade-in">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-serif text-lg font-semibold text-stone-900">Details</h3>
                  <button
                    onClick={() => setSelectedImage(null)}
                    className="text-stone-400 hover:text-stone-600"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <img
                  src={selectedImage.url}
                  alt={selectedImage.filename}
                  className="w-full aspect-square object-cover rounded-lg mb-4"
                />

                <div className="space-y-3 text-sm">
                  <div>
                    <p className="font-sans text-xs text-stone-500 uppercase">Filename</p>
                    <p className="font-sans text-stone-900 break-all">{selectedImage.filename}</p>
                  </div>
                  <div>
                    <p className="font-sans text-xs text-stone-500 uppercase">Size</p>
                    <p className="font-sans text-stone-900">{formatFileSize(selectedImage.size)}</p>
                  </div>
                  <div>
                    <p className="font-sans text-xs text-stone-500 uppercase">URL</p>
                    <div className="flex items-center gap-2 mt-1">
                      <input
                        type="text"
                        value={`${window.location.origin}${selectedImage.url}`}
                        readOnly
                        className="flex-1 text-xs bg-stone-50 border border-stone-200 rounded px-2 py-1"
                      />
                      <button
                        onClick={() => copyUrl(selectedImage.url)}
                        className="p-1.5 bg-stone-100 rounded hover:bg-stone-200"
                      >
                        {copiedUrl === selectedImage.url ? (
                          <Check className="w-4 h-4 text-green-600" />
                        ) : (
                          <Copy className="w-4 h-4 text-stone-600" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => handleDelete(selectedImage.filename)}
                  data-testid="delete-selected-image"
                  className="w-full mt-6 flex items-center justify-center gap-2 text-red-600 border border-red-200 rounded-full py-2 hover:bg-red-50 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete Image
                </button>
              </div>
            ) : (
              <div className="bg-white p-8 border border-stone-200 rounded-lg text-center">
                <Image className="w-8 h-8 mx-auto text-stone-300 mb-2" />
                <p className="font-sans text-sm text-stone-500">
                  Click an image to see details
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
